import { useState } from "react";
import { Sparkle, ArrowsOutSimple, PaperPlaneRight, CircleNotch, ArrowRight, WarningCircle } from "@phosphor-icons/react";
import MicButton from "@/components/MicButton";

const arr = (x) => (Array.isArray(x) ? x : x ? [x] : []);

function ClarityItem({ c, index, value, onChange }) {
  const provided = !!(value && value.trim());
  return (
    <div data-testid={`clarity-${index}`} className="px-4 py-3.5">
      <div className="flex items-center gap-2.5">
        <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: provided ? "hsl(var(--tag-preference))" : "hsl(var(--tag-assumption))" }} />
        <p className="flex-1 text-sm font-medium leading-snug">{c.concern}</p>
        <span data-testid={`clarity-status-${index}`} className="shrink-0 text-[10px] font-medium uppercase tracking-wide" style={{ color: provided ? "hsl(var(--tag-preference))" : "hsl(var(--muted-foreground))" }}>
          {provided ? "✓ provided" : "needs input"}
        </span>
      </div>
      {c.evidence_that_would_help && <p className="mt-1 pl-[1.05rem] text-xs leading-relaxed text-muted-foreground">{c.evidence_that_would_help}</p>}
      <div className="relative ml-[1.05rem] mt-2 w-[calc(100%-1.05rem)]">
        <textarea
          data-testid={`clarity-input-${index}`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          placeholder="Add what you know…"
          className="w-full resize-y rounded-sm border border-input bg-muted/30 p-2 pr-10 text-[13px] leading-relaxed outline-none transition-colors focus:border-foreground focus:bg-background focus:ring-1 focus:ring-ring"
        />
        <MicButton size={15} className="absolute right-2 top-2" onResult={(t) => onChange(value ? value.trim() + " " + t : t)} />
      </div>
    </div>
  );
}

export default function ExecutiveSummary({ map, onViewFull, onProvideClarity, onContinue, unansweredCount = 0, updating }) {
  const [answers, setAnswers] = useState({});
  if (!map) return null;
  const wellRepresented = arr(map.well_represented);
  const opportunities = arr(map.missing_or_underrepresented).slice(0, 5);

  const providedCount = Object.values(answers).filter((v) => v && v.trim()).length;
  const submit = () => {
    const parts = opportunities
      .map((c, i) => ({ q: c.concern, a: (answers[i] || "").trim() }))
      .filter((x) => x.a);
    if (!parts.length) return;
    const instruction = "The CEO has provided additional information for areas that needed more clarity:\n\n" +
      parts.map((p) => `• On "${p.q}":\n${p.a}`).join("\n\n");
    onProvideClarity?.(instruction, parts.map((p) => p.q));
    setAnswers({});
  };

  return (
    <div data-testid="executive-summary" className="w-full space-y-5">
      {/* Formulation */}
      <section data-testid="summary-problem" className="rounded-lg border border-border bg-muted/50 p-6">
        <div className="mb-4 flex items-baseline gap-4">
          <span className="font-mono text-xs text-muted-foreground">01</span>
          <h2 className="font-serif text-3xl leading-tight sm:text-4xl">The Problem</h2>
        </div>
        <p className="text-[17px] leading-relaxed text-foreground/90">{map.current_challenge_formulation}</p>
      </section>

      {/* Aim */}
      <section data-testid="summary-aim" className="rounded-lg border border-border bg-muted/50 p-6">
        <div className="mb-4 flex items-baseline gap-4">
          <span className="font-mono text-xs text-muted-foreground">02</span>
          <h2 className="font-serif text-3xl leading-tight sm:text-4xl">What You're Trying To Achieve</h2>
        </div>
        <p className="text-[17px] leading-relaxed text-foreground/90">{map.current_developmental_aim}</p>
      </section>

      {/* Well represented */}
      {wellRepresented.length > 0 && (
        <section data-testid="summary-clear" className="rounded-lg border border-border bg-muted/50 p-6">
          <div className="mb-4 flex items-baseline gap-4">
            <span className="font-mono text-xs text-muted-foreground">03</span>
            <h2 className="font-serif text-3xl leading-tight sm:text-4xl">What's Clear So Far</h2>
          </div>
          <ul className="grid gap-2.5 sm:grid-cols-2">
            {wellRepresented.map((w, i) => (
              <li key={i} data-testid={`summary-well-${i}`} className="flex gap-3 text-[15px] leading-relaxed">
                <Sparkle size={16} weight="fill" className="mt-1 shrink-0" style={{ color: "hsl(var(--tag-preference))" }} />
                <span>{w.point || w}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Where We Need More Clarity */}
      {opportunities.length > 0 && (
        <section data-testid="summary-clarity" className="rounded-lg border border-border bg-muted/50 p-6">
          <div className="mb-2 flex items-baseline gap-4">
            <span className="font-mono text-xs text-muted-foreground">04</span>
            <h2 className="font-serif text-3xl leading-tight sm:text-4xl">Where We Need More Clarity</h2>
          </div>
          <p className="mb-5 text-sm leading-relaxed text-muted-foreground">
            Each item below is something the analysis needs to get right. Provide what you know — without it,
            the problem space and any solutions built on it will be incomplete. Open an item, add the information,
            then update the analysis.
          </p>
          <div className="divide-y divide-border rounded-sm border border-border bg-card">
            {opportunities.map((c, i) => (
              <ClarityItem key={i} c={c} index={i} value={answers[i]} onChange={(v) => setAnswers((a) => ({ ...a, [i]: v }))} />
            ))}
          </div>
          <button
            data-testid="update-analysis-btn"
            onClick={submit}
            disabled={providedCount === 0 || updating}
            className="mt-5 inline-flex items-center gap-2 rounded-sm border border-primary bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/85 disabled:opacity-40"
          >
            {updating ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneRight size={16} weight="bold" />}
            {updating ? "Updating the analysis…" : `Update the analysis${providedCount ? ` (${providedCount} provided)` : ""}`}
          </button>
        </section>
      )}

      {/* Continue to Problem Space */}
      <section data-testid="continue-section" className="rounded-lg border border-border bg-muted/50 p-6">
        {unansweredCount > 0 ? (
          <div className="flex items-start gap-3 rounded-sm border border-amber-500/50 bg-amber-500/10 px-4 py-3.5">
            <WarningCircle size={18} className="mt-0.5 shrink-0 text-amber-600" weight="fill" />
            <div className="flex-1">
              <p className="text-sm leading-relaxed text-foreground/90">
                You still have <strong className="font-medium">{unansweredCount} question{unansweredCount > 1 ? "s" : ""}</strong> that need answers.
                Answer them above and update the analysis for the most complete result — or move on and come back to them later.
              </p>
              <button
                data-testid="move-on-btn"
                onClick={onContinue}
                disabled={updating}
                className="mt-3 inline-flex items-center gap-2 rounded-sm border border-border bg-background px-5 py-2.5 text-sm font-medium transition-colors hover:bg-muted disabled:opacity-40"
              >
                Move on for now <ArrowRight size={15} weight="bold" />
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p className="mb-3 text-sm leading-relaxed text-muted-foreground">
              Everything the analysis needs is in place. You're ready to map the problem space.
            </p>
            <button
              data-testid="continue-to-problem-space-btn"
              onClick={onContinue}
              disabled={updating}
              className="inline-flex items-center gap-2 rounded-sm border border-primary bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/85 disabled:opacity-40"
            >
              Continue to Problem Space <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        )}
      </section>

      {/* View full map */}
      <div className="rounded-lg border border-border bg-muted/50 p-6">
        <button
          data-testid="view-full-map-btn"
          onClick={onViewFull}
          className="inline-flex items-center gap-2 rounded-sm border border-border px-5 py-3 text-sm font-medium transition-colors hover:bg-muted"
        >
          <ArrowsOutSimple size={16} weight="bold" /> View the complete analysis
        </button>
      </div>
    </div>
  );
}
